import { Calendar, ImageOff } from "lucide-react";
import { Link } from "@inertiajs/react";

export type ArticleCard = {
  title: string;
  slug: string;
  tags: string[];
  meta_description: string | null;
  featured_image_url: string | null;
  published_at: string;
};

const BlogSection = ({ articles }: { articles: ArticleCard[] }) => {
  if (articles.length === 0) return null;

  return (
    <div className="car-section">
      <div className="max-w-screen-xl w-full mx-auto">
        <SectionHeader />
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-6">
          {articles.map((article) => (
            <ArticleCardItem key={article.slug} article={article} />
          ))}
        </div>
        <ViewMoreLink />
      </div>
    </div>
  );
};

export default BlogSection;

const SectionHeader = () => (
  <div className="flex flex-col items-center gap-2 mt-16 mb-4">
    <span className="text-2xl font-medium uppercase tracking-[1px]">
      Nos derniers articles
    </span>
    <hr className="border-primary w-20 border-2 rounded-full" />
  </div>
);

const ArticleCardItem = ({ article }: { article: ArticleCard }) => (
  <Link
    href={`/blog/articles/${article.slug}`}
    className="group bg-white rounded-xl border border-gray-100 shadow-xs overflow-hidden hover:shadow-md transition-shadow flex flex-col no-underline text-inherit"
  >
    <ArticleThumbnail url={article.featured_image_url} title={article.title} />
    <div className="p-5 flex flex-col gap-3 flex-1">
      <ArticleTags tags={article.tags} />
      <h3 className="text-lg font-bold leading-snug group-hover:underline text-balance">
        {article.title}
      </h3>
      {article.meta_description && (
        <p className="text-sm text-muted-foreground leading-6 line-clamp-3">
          {article.meta_description}
        </p>
      )}
      <ArticleDate date={article.published_at} />
    </div>
  </Link>
);

const ArticleThumbnail = ({
  url,
  title,
}: {
  url: string | null;
  title: string;
}) =>
  url ? (
    <img
      src={url}
      alt={title}
      loading="lazy"
      className="w-full h-[180px] object-cover block bg-gray-100"
    />
  ) : (
    <div
      className="w-full h-[180px] flex items-center justify-center bg-gray-100 text-gray-400 select-none"
      aria-hidden="true"
    >
      <ImageOff className="w-9 h-9" strokeWidth={1.5} />
    </div>
  );

const ArticleTags = ({ tags }: { tags: string[] }) => {
  if (tags.length === 0) return null;
  return (
    <div className="flex flex-wrap gap-2">
      {tags.map((tag) => (
        <span
          key={tag}
          className="bg-[#EFECFF] text-[#412FB2] rounded-md px-2.5 py-1 text-xs font-medium"
        >
          {tag}
        </span>
      ))}
    </div>
  );
};

const ArticleDate = ({ date }: { date: string }) => {
  const formattedDate = new Intl.DateTimeFormat("fr-FR", {
    day: "numeric",
    month: "long",
    year: "numeric",
  }).format(new Date(date));
  return (
    <div className="mt-auto pt-1 flex items-center gap-2 text-sm text-muted-foreground">
      <Calendar className="w-4 h-4" />
      <span>{formattedDate}</span>
    </div>
  );
};

const ViewMoreLink = () => (
  <div className="flex justify-end w-full mt-4">
    <Link
      href="/blog"
      className="text-sm font-semibold text-primary hover:underline no-underline"
    >
      Voir plus →
    </Link>
  </div>
);
